import angular from 'angular'
import isEqual from 'lodash/isEqual'
import { ApiUser } from '@vend/types'
import { TOUR_TYPE } from '../constants'

import { EventEmitter } from '../util/event-emitter'
import {
  PosState,
  clearQkLayoutDuplication,
  selectActiveOutletId,
  selectActiveRegisterId,
  selectPosState,
  setQkLayoutDuplication,
  setUser,
  updatePosState,
} from '../state/slices/posState/posStateSlice'
import { store, whenStoreBootstrapped } from '../state/store'
import { wrDebugToolsModuleRegistry } from '../factories/application/wr-debug-tools'
import { clientApiEventBus } from './client-api-event-bus'

type EventPayload<K extends keyof PosState> = {
  prevValue: PosState[K]
  newValue: PosState[K]
}

// There is a change event for each object key in PosState.
type Events = Required<
  {
    [K in keyof PosState as `${K}Change`]: [EventPayload<K>]
  }
>

/**
 * Manages the current state of the POS.
 *
 * @class PosStateManager
 * @constructor
 * @extends EventEmitter
 */
export class PosStateManager extends EventEmitter<Events> {
  constructor() {
    super()

    wrDebugToolsModuleRegistry.register('posStateManager')

    whenStoreBootstrapped().then(() => {
      clientApiEventBus.emitEvent({
        eventName: clientApiEventBus.EVENTS.OUTLET_SET,
        data: { outlet_id: selectActiveOutletId(store.getState()) },
      })
      clientApiEventBus.emitEvent({
        eventName: clientApiEventBus.EVENTS.REGISTER_SET,
        data: { register_id: selectActiveRegisterId(store.getState()) },
      })
    })
  }

  /**
   * Temporary accessor to the PosState in Redux so we can continue to access as this._posState[property] while we
   * migrate this state into Redux and refactor all the places accessing it.
   */
  private get _posState(): PosState {
    return selectPosState(store.getState())
  }

  /**
   * Initialises the PosStateManager by waiting until the current state saved in IndexedDB has been successfully
   * loaded into the Redux store.
   *
   * @method init
   *
   * @return {Promise} A promise resolved with the PosStateManager instance once initialisation is complete
   */
  public init() {
    return whenStoreBootstrapped().then(() => this)
  }

  /**
   * Get the current client ID.
   *
   * @method getClientId
   *
   * @return {String} the current client ID or undefined if it is not set
   */
  public getClientId() {
    return this._posState.clientId
  }

  /**
   * Set the current client ID. Emits the `clientIdChange` event with the previous and new values
   * in the `event.prevValue` and `event.newValue` properties respectively.
   *
   * @method setClientId
   *
   * @param clientId {String}
   *      The current client ID
   */
  public setClientId(clientId: string) {
    this._setProperty('clientId', clientId)
  }

  /**
   * Get the current active user.
   *
   * @method getUser
   *
   * @return {Object} the current active user or undefined if it is not set
   */
  public getUser() {
    return this._posState.user
  }

  /**
   * Set the current active user.
   *
   * @method setUser
   *
   * @param user {Object}
   *      The current active user
   */
  public setUser(user: ApiUser | null) {
    store.dispatch(setUser(user))
  }

  /**
   * Get the current active outlet's ID.
   *
   * @method getOutletId
   *
   * @return {String} the ID of the current active outlet or undefined if it is not set
   */
  public getOutletId() {
    return this._posState.outletId
  }

  /**
   * Set the current active outlet's ID. Emits the `outletIdChange` event with the previous and new values in
   * the `event.prevValue` and `event.newValue` properties respectively.
   *
   * @method setOutletId
   *
   * @param outletId {String}
   *      The ID of the current active outlet
   */
  public setOutletId(outletId: string) {
    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.OUTLET_SET,
      data: { outlet_id: outletId },
    })
    this._setProperty('outletId', outletId)
  }

  /**
   * Get the current active register's ID.
   *
   * @method getRegisterId
   *
   * @return {String} the current active register's ID or undefined if it is not set
   */
  public getRegisterId() {
    return this._posState.registerId
  }

  /**
   * Set the current active register's ID. Emits the `registerIdChange` event with the previous and new values
   * in the `event.prevValue` and `event.newValue` properties respectively.
   *
   * @method setRegisterId
   *
   * @param registerId {String}
   *      The current active register's ID
   */
  public setRegisterId(registerId: string) {
    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.REGISTER_SET,
      data: { register_id: registerId },
    })
    this._setProperty('registerId', registerId)
  }

  /**
   * Get the last used receipt number. DEPRECATED Use getLastUsedReceiptNumber within registerManager. Method is
   * required while we transition from using this key (LAST_USED_RECEIPT_NUMBER) to new
   * (RECEIPT_NUMBERS_FOR_REGISTERS). @todo Remove once most retailers are on v13.
   *
   * @method getLastUsedReceiptNumber
   *
   * @return {Number} the last used receipt number or undefined if it is not set
   */
  public getLastUsedReceiptNumber() {
    return this._posState.lastUsedReceiptNumber
  }

  /**
   * Get the last known receipt numbers for each register.
   *
   * @method getLastUsedReceiptNumbersForRegisters
   *
   * @return {Object} Key value pair of register id's and their last used receipt number
   */
  public getLastUsedReceiptNumbersForRegisters() {
    return this._posState.receiptNumbersForRegisters
  }

  /**
   * Set the receipt number for registers
   *
   * @method setLastUsedReceiptNumbersForRegisters
   *
   * @param receiptNumbersForRegisters {Object}
   *        The key value pair of register id's and their last used receipt number
   */
  public setLastUsedReceiptNumbersForRegisters(
    receiptNumbersForRegisters: Record<string, number>
  ) {
    this._setProperty('receiptNumbersForRegisters', receiptNumbersForRegisters)
  }

  /**
   * Get the current active sale's local ID.
   *
   * @method getActiveSaleLocalId
   *
   * @return {String} the local ID of the current active sale or undefined if there is no active sale
   */
  public getActiveSaleLocalId() {
    return this._posState.activeSaleLocalId
  }

  /**
   * Set the current active sale's local ID. Emits the `activeSaleLocalIdChange` event with the previous and
   * new values in the `event.prevValue` and `event.newValue` properties respectively.
   *
   * @method setActiveSaleLocalId
   *
   * @param activeSaleLocalId {String}
   *      The localId of the current active sale
   */
  public setActiveSaleLocalId(activeSaleLocalId: string | null) {
    this._setProperty('activeSaleLocalId', activeSaleLocalId)
  }

  /**
   * Get the registers training mode status
   *
   * @method getTrainingModeEnabled
   *
   * @return {Boolean} true if the register is in training mode, false otherwise
   */
  public getTrainingModeEnabled() {
    return Boolean(this._posState.trainingModeEnabled)
  }

  /**
   * Sets the register to be in training mode
   *
   * @method setTrainingModeEnabled
   *
   * @param  enableTrainingMode {Boolean}
   *         true if the register should be placed in training mode, false otherwise
   */
  public setTrainingModeEnabled(enableTrainingMode: boolean) {
    this._setProperty('trainingModeEnabled', enableTrainingMode)
  }

  /**
   * Returns an array of user IDs that have recently logged in
   *
   * @method getRecentLoggedUserIds
   *
   * @return {Array} Ordered array of user IDs sorted by last login
   */
  public getRecentLoggedUserIds() {
    return this._posState.recentLoggedUserIds || []
  }

  /**
   * Get the registers training mode tour type
   *
   * @method getTourType
   *
   * @return {String} The tour type constant
   */
  public getTourType() {
    return this._posState.tourType
  }

  /**
   * Sets the registers training mode tour type
   *
   * @method setTourType
   *
   * @param  tourType {String}
   *         The tour type constant
   */
  public setTourType(tourType: TOUR_TYPE) {
    this._setProperty('tourType', tourType)
  }

  /**
   * Get the registers training mode state
   *
   * @method getTrainingModeOnboardingState
   *
   * @return {Boolean} true if the register is in training mode on-boarding state, false otherwise
   */
  public getTrainingModeOnboardingState() {
    return this._posState.trainingModeOnboardingState
  }

  /**
   * Sets the register's training mode on-boarding state
   *
   * @method setTrainingModeOnboardingState
   * @param  onboardingState {Boolean}
   *         true if the register should be placed in training mode on-boarding state, false otherwise
   */
  public setTrainingModeOnboardingState(onboardingState: boolean) {
    this._setProperty('trainingModeOnboardingState', onboardingState)
  }

  /**
   * Get the current tour enabled status. Whether the register's training mode has a tour in progress.
   *
   * @method getTourEnabled
   *
   * @return {Boolean} true if the register has a tour in progress, false otherwise
   */
  public getTourEnabled() {
    return this._posState.tourEnabled
  }

  /**
   * Set the register to enter or leave a tour.
   *
   * @method setTourEnabled
   *
   * @param  enableTour {Boolean}
   *         true if the register should trigger a tour, false otherwise
   */
  public setTourEnabled(enableTour: boolean) {
    this._setProperty('tourEnabled', enableTour)
  }

  /**
   * Whether the SSBO onboarding guided tour is running. Persisted, so the tour
   * resumes after a page refresh.
   *
   * @method getSsboTourActive
   *
   * @return {Boolean} true if the SSBO guided tour is active
   */
  public getSsboTourActive() {
    return Boolean(this._posState.ssboTourActive)
  }

  /**
   * Start or end the SSBO onboarding guided tour.
   *
   * @method setSsboTourActive
   *
   * @param  active {Boolean} true to start the tour, false to end it
   */
  public setSsboTourActive(active: boolean) {
    this._setProperty('ssboTourActive', active)
  }

  /**
   * End the SSBO onboarding guided tour if it is currently active.
   * Used at each deviation point that should stop the tour.
   *
   * @method endSsboTourIfActive
   */
  public endSsboTourIfActive() {
    if (this.getSsboTourActive()) {
      this.setSsboTourActive(false)
    }
  }

  /**
   * Get the state of initial sync completion.
   *
   * @method getInitialSyncComplete
   *
   * @return {Boolean} true if the initial sync has been completed, false otherwise
   */
  public getInitialSyncComplete() {
    return this._posState.initialSyncComplete
  }

  /**
   * Set the initial sync completion state.
   *
   * @method setInitialSyncComplete
   *
   * @param  initialSyncComplete {Boolean}
   *         State of initial sync completion - true if completed, false otherwise
   */
  public setInitialSyncComplete(initialSyncComplete: boolean) {
    this._setProperty('initialSyncComplete', initialSyncComplete)
  }

  /**
   * Indicates when a quick keys duplication is in progress for the given button layout ID (the ID of the new layout).
   *
   * @method getQkLayoutDuplication
   *
   * @param  buttonLayoutId {String}
   *         ID of the new button layout being duplicated
   *
   * @return {Boolean} true if the given button layout is in the process of being populated by duplication
   */
  public getQkLayoutDuplication(buttonLayoutId: string) {
    const duplication = this._posState.qkLayoutDuplication || {}
    return Boolean(duplication[buttonLayoutId])
  }

  /**
   * Get the list of button layouts currently being duplicated.
   *
   * @method getAllQkLayoutDuplications
   *
   * @return {Array<String>} array of button layout IDs for the layouts being duplicated
   */
  public getAllQkLayoutDuplications() {
    return Object.keys(this._posState.qkLayoutDuplication || {})
  }

  /**
   * Clear the duplication state of the given button layout ID.
   *
   * @method clearQkLayoutDuplication
   *
   * @param  buttonLayoutId {String}
   *         ID of the button layout that is no longer being tracked as duplicating (either completed or timed out)
   */
  public clearQkLayoutDuplication(buttonLayoutId: string) {
    store.dispatch(clearQkLayoutDuplication(buttonLayoutId))
  }

  /**
   * Set the quick keys duplication state for the given button layout ID.
   *
   * @method setQkLayoutDuplication
   *
   * @param  buttonLayoutId {String}
   *         ID of the new button layout being duplicated
   *
   * @param  [qkLayoutDuplication=true] {Boolean}
   *         Whether or not the given button layout is still being duplicated
   */
  public setQkLayoutDuplication(buttonLayoutId: string) {
    store.dispatch(setQkLayoutDuplication(buttonLayoutId))
  }

  /**
   * Get the feature flag values.
   *
   * @method getFeatures
   *
   * @return {Map<String, Boolean>} Map of feature name to value (true/false)
   */
  public getFeatures() {
    return this._posState.features
  }

  /**
   * Set the feature flag values.
   *
   * @method setFeatures
   *
   * @param  features {Map<String, Boolean>}
   *         Map of feature name to value (true/false)
   */
  public setFeatures(features: Record<string, boolean>) {
    this._setProperty('features', features)
  }

  /**
   * Get all open register reminders seen.
   *
   * @method getAllOpenRegisterRemindersSeen
   *
   * @return {Map<String, String>} Map of register id to openining sequence id.
   */
  public getAllOpenRegisterRemindersSeen(): Record<string, string> {
    const openRegisterRemindersSeen = this._posState.registerOpenRemindersSeen

    return { ...openRegisterRemindersSeen }
  }

  /**
   * Set all open register reminders seen.
   *
   * @method setAllOpenRegisterRemindersSeen
   *
   * @param {Map<String, String>} openRegisterReminderSeen.
   */
  public setAllOpenRegisterRemindersSeen(openRegisterReminderSeen: $TSFixMe) {
    this._setProperty('registerOpenRemindersSeen', openRegisterReminderSeen)
  }

  /**
   * Check if promotions helper has been displayed.
   *
   * @method getPromotionSuggestion
   */
  public getPromotionSuggestion() {
    return this._posState.promoHelperSuggestion
  }

  /**
   * Check if promotions helper has been displayed.
   *
   * @method setPromotionSuggestion
   */
  public setPromotionSuggestion(id: string) {
    this._setProperty('promoHelperSuggestion', id)
  }

  /**
   * Check if promotions helper has been displayed.
   *
   * @method getPromotionsHelperClicked
   */
  public getPromotionsHelperClicked() {
    return this._posState.promoHelperClicked
  }

  /**
   * Check if promotions helper has been displayed.
   *
   * @method setPromotionsHelperClicked
   */
  public setPromotionsHelperClicked() {
    this._setProperty('promoHelperClicked', true)
  }

  /**
   * Reset promotions helper values
   *
   * @method resetPromotionsHelperValues
   */
  public resetPromotionsHelperValues() {
    this._setProperty('promoHelperSuggestion', undefined)
    this._setProperty('promoHelperClicked', false)
  }

  // ============================== Private API ============================== //

  /**
   * Set the value of a register state property.
   *
   * @private
   * @method _setProperty
   *
   * @param key
   *      The key that the state property is stored in the database against
   *
   * @param value
   *      The value to set the state property to
   */
  private _setProperty<K extends keyof PosState>(key: K, value: PosState[K]) {
    const prevValue = this._posState[key]

    store.dispatch(updatePosState({ key, value } as any))

    if (!isEqual(value, prevValue)) {
      this.emit(`${key}Change` as any, {
        prevValue,
        newValue: value,
      })
    }
  }
}

export const posStateManager = new PosStateManager()

/**
 * Manages the current state of the POS.
 *
 * @module posStateManager
 */
angular
  .module('webregister.managers')
  .factory('posStateManager', () => posStateManager)
